module Client.Scaffold

open System
open Fable.React.Props
open Fable.React
open Shared.Domain

type Unescaped =
    { __html: string }

let stackTheme (theme:Theme) : CSSProp list =
    [
        for entry in theme do
            match entry with
            | Background colour -> yield BackgroundColor colour
            | Colour colour -> yield Color colour    
            | Font font -> yield FontFamily font
            | FontSize size -> yield CSSProp.FontSize size                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      
    ]

let castList (input:HTMLAttr list) : IHTMLProp list =
    input |> List.map (fun x -> x :> IHTMLProp)

let appendTheme (existing: CSSProp list) (inheritedTheme: CSSProp list) =
    Style (existing@inheritedTheme) :> IHTMLProp

let setText text (element:IHTMLProp list -> ReactElement list -> ReactElement) props contents =
    match text with
    | RawText (Utils.NOTEMPTY txt) -> 
        element props (str txt::contents)
    | Markdown (Utils.NOTEMPTY txt) -> 
        element ((DangerouslySetInnerHTML ({ __html = txt }) :> IHTMLProp)::props) contents
    | PageNavigation -> failwith "Implement server-side"
    | YouTubeEmbed (link, h, w, full) -> 
        let vid = 
            iframe [ 
                ClassName "youtube-player" 
                HTMLAttr.Type "text/html"
                HTMLAttr.Width w
                HTMLAttr.Height h
                Src link
                FrameBorder "0"
                HTMLAttr.AllowFullScreen full
            ] []
        element props (vid::contents)
    | _ -> element props contents


let renderBlock (inheritedTheme: CSSProp list) (block: Block) =
    div [ClassName ("block" + if block.Justification = Justification.Centered then " text-center" else ""); appendTheme inheritedTheme (stackTheme block.Theme)] [
        match block.Header with
        | None -> ()
        | Some (accent, h) ->   
            let propList =
                match accent with
                | Inherited -> []
                | Dark -> [ClassName "dark"]
                | Light -> [ClassName "light"]
                | Blue -> [ClassName "blue"]
                | Grey -> [ClassName "grey"]
                | DarkRed -> [ClassName "red"]
                | Turquoise -> [ClassName "turquoise"]
            yield setText h h2 (castList propList) []     
        yield setText (block.Content) div [ClassName "paragraph"] []    
    ]

let renderHeader (inheritedTheme: CSSProp list) (block: Block) =
    let accent, hVal = block.Header.Value
    
    let accentClass =
        match accent with
        | Inherited -> ""
        | Dark -> " dark"
        | Light  -> " light"
        | Blue -> " blue"
        | Grey -> " grey"
        | DarkRed -> " red"
        | Turquoise -> " turquoise"

    setText hVal div ([ClassName ("header" + accentClass); appendTheme inheritedTheme (stackTheme block.Theme)]) []

let renderColumn (inheritedTheme: CSSProp list) (blocks: Block list) =
    blocks |> List.map (renderBlock inheritedTheme)

let renderBreadcrumbs (page: GenericPage) (hierarchy: Hierarchy) =
    [
        nav [ ClassName "breadcrumb" ] [
            ol [ ClassName "breadcrumb" ] [
                for node in hierarchy.Breadcrumbs do
                    yield li [ ClassName "breadcrumb-item" ] [  a [ Href (match node.Id with Slug s -> s) ] [ str node.Name ] ]
                                 
                yield li [ ClassName "breadcrumb-item-active" ] [ str page.Location.Name ]
            ]
        ]
    ]

let renderPage (page: Page) (hierarchy: Hierarchy option) =
    match page with
    | Generic p ->
        let pageTheme = stackTheme p.Theme
        
        let container =
            match p.PageWidth with
            | WholePage -> "generic"
            | Centered -> "generic container"
        [
            div [ ClassName container ] [
                match hierarchy with
                | None -> ()
                | Some hier -> yield! renderBreadcrumbs p hier
                
                match p.Header with
                | Some header when header.Header.IsSome -> 
                    yield renderHeader pageTheme header
                | _ -> ()

                for block in p.TextBlocks do
                    yield div [ ClassName "block" ] [
                        match block with
                        | SingleColumn (_, col) -> yield! renderColumn pageTheme col
                        | TwoColumn (_,l,r) ->
                            yield div [ ClassName "row" ] [
                                div [ ClassName "col-sm-6" ] [
                                    yield! renderColumn pageTheme l
                                ]
                                div [ ClassName "col-sm-6" ] [
                                    yield! renderColumn pageTheme r
                                ]
                            ] 
                        | MainAndSideBar (_,l,r) ->
                            yield div [ ClassName "row" ] [
                                div [ ClassName "col-sm-9" ] [
                                    yield! renderColumn pageTheme l
                                ]
                                div [ ClassName "col-sm-3" ] [
                                    yield! renderColumn pageTheme r
                                ]
                            ]
                        | CardList (_, shape, cards) ->
                            yield div [ ClassName "row" ] [
                                for (img', card) in cards do
                                    yield div [ ClassName "col-sm-6" ] [
                                        div [ ClassName "col-sm-4 col-xs-12" ] [
                                            img [ 
                                                Alt img'.Alt
                                                Src img'.Url
                                                Title img'.HoverText
                                                ClassName (
                                                    match shape with
                                                    | Rectangle -> "img-responsive"
                                                    | RoundedRectangle -> "img-rounded"
                                                    | Circle -> "img-circle"
                                                    | Thumbnail -> "img-thumbnail")
                                            ]
                                        ]
                                        div [ ClassName "col-xs-8 col-sm-12" ] [
                                            yield! renderColumn pageTheme [card]
                                        ] 
                                    ]
                            ]                
                    ]
            ]
        ]
